import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { PAGE_SIZE, type RecentActivityEntry } from './recentActivityConstants'

interface UseRecentActivityParams {
  page: number
  dateFrom: Date
  dateTo: Date
  memberQuery?: string
  activityTypes?: string[]
  actorIds?: string[]
  isAnonymised?: boolean
}

interface RecentActivityResult {
  entries: RecentActivityEntry[]
  totalCount: number
}

interface RpcRow {
  id: string
  type: string
  stamps_delta: number
  rewards_delta: number
  created_at: string
  actor_name: string | null
  actor_role: string | null
  receipt_id: string | null
  correction_reason: string | null
  correction_note: string | null
  device_name: string | null
  reward_item_name: Record<string, string> | null
  loyalty_config_snapshot: { earning_method?: string; amount_per_stamp?: number; trigger?: string; stamp_target_before?: number; stamp_target_after?: number; stamps_before?: number; stamps_after?: number } | null
  member_uuid: string
  member_first_name: string | null
  member_last_name: string | null
  member_email: string
  member_display_id: string | null
}

function getFullName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ') || '—'
}

function mapRow(row: RpcRow, lang: string): RecentActivityEntry {
  const nameObj = row.reward_item_name
  const rewardItemName = nameObj ? (nameObj[lang] || nameObj.en || null) : null

  return {
    id: row.id,
    type: row.type as RecentActivityEntry['type'],
    stampsDelta: row.stamps_delta,
    rewardsDelta: row.rewards_delta,
    createdAt: row.created_at,
    actorName: row.actor_name,
    actorRole: row.actor_role,
    memberName: getFullName(row.member_first_name, row.member_last_name),
    memberEmail: row.member_email,
    memberId: row.member_uuid,
    memberDisplayId: row.member_display_id,
    receiptId: row.receipt_id,
    correctionReason: row.correction_reason,
    correctionNote: row.correction_note,
    rewardItemName: rewardItemName,
    earningMethod: row.loyalty_config_snapshot?.earning_method ?? null,
    amountPerStamp: row.loyalty_config_snapshot?.amount_per_stamp ?? null,
    deviceName: row.device_name,
    trigger: row.loyalty_config_snapshot?.trigger ?? null,
    stampTargetBefore: row.loyalty_config_snapshot?.stamp_target_before ?? null,
    stampTargetAfter: row.loyalty_config_snapshot?.stamp_target_after ?? null,
    stampsBeforeReduction: row.loyalty_config_snapshot?.stamps_before ?? null,
    stampsAfterReduction: row.loyalty_config_snapshot?.stamps_after ?? null,
  }
}

async function fetchRecentActivity(params: UseRecentActivityParams, lang: string): Promise<RecentActivityResult> {
  const { data, error } = await supabase.rpc('get_recent_activity', {
    p_date_from: params.dateFrom.toISOString(),
    p_date_to: params.dateTo.toISOString(),
    p_page: params.page,
    p_page_size: PAGE_SIZE,
    p_member_query: params.memberQuery || null,
    p_activity_types: params.activityTypes ?? null,
    p_actor_ids: params.actorIds ?? null,
    p_is_anonymised: params.isAnonymised ?? null,
  })

  if (error) throw error

  const result = data as { entries: RpcRow[]; total_count: number }

  return {
    entries: result.entries.map((row) => mapRow(row, lang)),
    totalCount: result.total_count,
  }
}

export function useRecentActivity(params: UseRecentActivityParams, lang: string) {
  return useQuery({
    queryKey: [
      'recent-activity',
      params.page,
      params.dateFrom.toISOString(),
      params.dateTo.toISOString(),
      params.memberQuery,
      params.activityTypes,
      params.actorIds,
      params.isAnonymised,
    ],
    queryFn: () => fetchRecentActivity(params, lang),
  })
}